// @flow

import DOM from '../../util/dom';
import {extend, bindAll} from '../../util/util';
import {hexToRgb} from '../../util/color';
import {ContentVisibilityEvent, ContentRemoveEvent} from '../events';

type Options = {
    id: string,
};

const defaultOptions: Options = {
    id: 'roadsurfacelegend',
};
const roadSurfaceColorOptions = {
    notInRange: '#000',
    dryRoad: '#777',
    wetRoad: '#0f0',
    ice: '#FF0000',
    mixwater: '#00f',
};
const roadSurfaceLegendNames = {
    notInRange: 'Not in range',
    dryRoad: 'Dry road/Dew',
    wetRoad: 'Wet road/Melting snow',
    ice: 'Ice/Icing rain/Snow/Frost',
    mixwater: 'Mix water/Snow',
};
/**
 * The `RoadSurfaceLegendControl` adds the ability to show a legend for the road surface layer.
 *
 * @implements {IControl}
 *
 * @example
 * var RoadSurfaceLegendControl = new TrimbleMaps.RoadSurfaceLegendControl();
 * map.addControl(RoadSurfaceLegendControl, 'top-left');
 */
class RoadSurfaceLegendControl {
    _map: any;
    _options: Options;
    _container: HTMLElement;
    _listContainer: HTMLElement;

    constructor(options: Options) {
        this._options = extend({}, defaultOptions, options);

        bindAll(
            [
                '_onLoad',
                '_onSourceData',
                '_onSourceDataLoading',
                '_refreshRoadSurfaceList',
                '_filterFeatures',
                '_contentLayerVisibilityChanged',
                '_contentLayerRemoved',
            ],
            this
        );
    }

    onAdd(map: any) {
        this._map = map;
        this._container = DOM.create(
            'div',
            'trimblemaps-ctrl trimblemaps-ctrl-wrslegend'
        );
        this._listContainer = DOM.create('div', 'trimblemaps-ctrl-wrslist');
        const header = DOM.create('div', 'trimblemaps-ctrl-wrsheader');
        const h = DOM.create('h3');
        h.innerHTML = 'Road Surface';
        header.appendChild(h);
        this._container.appendChild(header);
        this._container.appendChild(this._listContainer);
        this._container.style.display = 'none';
        this._map.on('load', this._onLoad);

        // if layer is already loaded, match the visibility
        if (this._map.getLayer('weather_road_surface-weather_road_surface')) {
            const layerVisibility = this._map.getLayoutProperty(
                'weather_road_surface-weather_road_surface',
                'visibility'
            );
            this._container.style.display =
                layerVisibility === 'visible' ? 'block' : 'none';
            this._map.on('sourcedata', this._onSourceData);
            // this._refreshRoadSurfaceList();
        }

        this._map.on('content.visibility', this._contentLayerVisibilityChanged);
        this._map.on('content.remove', this._contentLayerRemoved);

        return this._container;
    }

    onRemove() {
        this._map.off('sourcedata', this._onSourceData);
        this._map.off('load', this._onLoad);

        this._map.off(
            'content.visibility',
            this._contentLayerVisibilityChanged
        );
        this._map.off('content.remove', this._contentLayerRemoved);

        DOM.remove(this._listContainer);
        DOM.remove(this._container);
        delete this._map;
    }

    _onLoad() {
        this._map.on('sourcedata', this._onSourceData);
    }

    _onSourceData(e: any) {
        if (e.sourceId === 'RoadSurface' && !e.sourceDataType) {
            this._refreshRoadSurfaceList();
            this._container.style.display = 'block';
        }
    }

    _refreshRoadSurfaceList() {
        const colorOptions = Object.values(roadSurfaceColorOptions);
        const legendNames = Object.values(roadSurfaceLegendNames);
        this._listContainer.innerHTML = '';

        if (colorOptions.length > 0) {
            colorOptions.forEach((e, i) => {
                if (e != null && typeof e === 'string') {
                    const rgbC = hexToRgb(e);
                    const span = DOM.create('span');
                    span.innerHTML = legendNames[i];
                    const colorSpan = DOM.create('span');
                    const div = DOM.create('div');
                    colorSpan.style.borderWidth = '4px';
                    colorSpan.style.borderStyle = 'solid';
                    colorSpan.style.display = 'inline-block';
                    // colorSpan.style.height = '15%';
                    colorSpan.style.width = '5%';
                    if (rgbC !== null) {
                        colorSpan.style.borderColor = `rgba(${rgbC.r},${rgbC.g},${rgbC.b},0.7)`;
                        colorSpan.style.backgroundColor = `rgba(${rgbC.r},${rgbC.g},${rgbC.b},0.2)`;
                    }
                    span.style.paddingLeft = '5%';
                    div.appendChild(colorSpan);
                    div.appendChild(span);

                    this._listContainer.appendChild(div);
                }
            });
        } else {
            const div = DOM.create('div');
            div.innerHTML = 'No Weather Road Surface Layer';
            this._listContainer.appendChild(div);
        }
    }

    _contentLayerVisibilityChanged(evt: ContentVisibilityEvent) {
        if (evt.id === 'weather_road_surface') {
            this._container.style.display = evt.isVisible ? 'block' : 'none';
        }
    }

    _contentLayerRemoved(evt: ContentRemoveEvent) {
        if (evt.id === 'weather_road_surface') {
            this._container.style.display = 'none';
        }
    }
}

export default RoadSurfaceLegendControl;
